/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { Hidden, breakpoints } from '@coursera/cds-core';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MobileCalendarLoader } from 'bundles/degree-home/components/v3-dpm/components/Calendar';
import { CalendarFilter } from 'bundles/degree-home/components/v3-dpm/components/Calendar/CalendarFilter';
import { MobileCalendarItemsList } from 'bundles/degree-home/components/v3-dpm/components/Calendar/MobileCalendarItemsList';
import { getCalendarEventsMap } from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import type { UseCalendarEventsQuery as UseCalendarEventsQueryData } from '../../lib/__queries__/__generated__/useCalendarEvents';
import useCalendarEvents from '../../lib/useCalendarEvents';
import { CalendarDayPicker } from './CalendarDayPicker';

type ContentProps = {
  loading: boolean;
  data?: UseCalendarEventsQueryData;
  error: Error | undefined | null;
  refetch: () => void;
};

export const MobileCalendarCardContent = (props: ContentProps) => {
  const { loading, data, error, refetch } = props;
  const { setRefetchCalendarItems, setCalendarEvents } = useDegreeContext();

  useEffect(() => {
    const events = data?.Degree?.queryById?.events;

    if (events) {
      setCalendarEvents(getCalendarEventsMap(events));
    }
    setRefetchCalendarItems(() => refetch);
  }, [data, refetch, setCalendarEvents, setRefetchCalendarItems]);

  if (loading) {
    return <MobileCalendarLoader />;
  }
  if (error) {
    return null;
  }

  return (
    <Card
      css={() => css`
        overflow: hidden;
        ${breakpoints.down('xs')} {
          padding: 0;
        }
      `}
      data-e2e="calendar-card-mobile"
    >
      <CalendarFilter mobile={true} />
      <CalendarDayPicker />
      <MobileCalendarItemsList />
    </Card>
  );
};

export const MobileCalendarCard = () => {
  const calendarEventsGql = useCalendarEvents();

  if (!calendarEventsGql) {
    return null;
  }

  const { data, loading, error, refetch } = calendarEventsGql;

  return (
    <Hidden mdUp>
      <MobileCalendarCardContent loading={loading} data={data} error={error} refetch={refetch} />
    </Hidden>
  );
};

export default MobileCalendarCard;
